import './locales/i18n';

import type { Facets } from '@ahd/shared/types/ahd-fleet-api/facets';
import type { MatomoSettings } from '@ahd/shared/types/tracking/matomo/website-settings/types';

import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';
import isUndefinedOrNull from '@ahd/shared/tools/is-undefined-or-null';
import outputConsole from '@ahd/shared/tools/output-console';
import { FrontendEnvironment } from '@ahd/shared/types/misc/environments';
import { POSTHOG_API_URL, POSTHOG_UI_URL } from '@ahd/shared/vars/posthog';
import { PostHogProvider } from 'posthog-js/react';
import { lazy, Suspense } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import sConfigs from './_configs/_configs';
import { DebugLevel } from './_configs/types';
import { getMatomoSettings } from './_tools/axios/matomo';
import { ConfigError, ConfigErrorCauses } from './_tools/errors/config';
import { errorHandler } from './_tools/errors/handler';
import pathIsDetailViewUrl from './_tools/is-detail-view-url';
import { ErrorAction } from './_tools/types/function-output-type';
import { DBLogSource } from './_tools/types/log';
import ErrorCardSkeleton from './components/error-card/skeleton';
import { getStorage, setStorage } from './hooks/use-session-storage/utils';
import modules from './modules/_definition/map';
import { type Modules } from './modules/_definition/types';
import ErrorHandler from './provider/ErrorHandler';
import ThemeProvider from './theme';
import initTheme from './theme/init-theme';
import { type AppContainer, type FleetSessionStorageSettings } from './types';
import { APPLICATION_NAME, defaultSessionStorage, ENVIRONMENT_FRONTEND, LOCAL_STORAGE_SETTINGS } from './vars';

const Entry = lazy(() => import('./module' /* webpackChunkName: "Entry" */));
const ErrorCard = lazy(() => import('./components/error-card' /* webpackChunkName: "ErrorCard" */));

let abortControllerFacets: AbortController;
const reactContent: Partial<Record<Modules, Root>> = {};

async function init() {
    let sessionStorage = getStorage<FleetSessionStorageSettings>(LOCAL_STORAGE_SETTINGS);
    if (typeof window !== 'undefined' && isUndefinedNullOrEmpty(sessionStorage)) {
        setStorage<FleetSessionStorageSettings>(LOCAL_STORAGE_SETTINGS, defaultSessionStorage);
        sessionStorage = defaultSessionStorage;
    }
    const settings = await sConfigs(undefined);
    if (settings?.general?.debug === DebugLevel.CONFIG || settings?.general?.debug >= DebugLevel.INFO) {
        outputConsole('log', 'index', 'settings', settings);
    }
    const theme = initTheme(settings, undefined, false);
    if (settings?.general?.debug === DebugLevel.CONFIG || settings?.general?.debug >= DebugLevel.INFO) {
        outputConsole('log', 'index', 'theme', theme);
    }

    const containers: AppContainer[] = [];
    try {
        const isDetail = pathIsDetailViewUrl(location, settings?.general?.disableSeoRoutes, settings?.general?.searchPath);

        for (let i = 0; i < modules?.length; i++) {
            const m = modules?.[i];
            const rawRootContainer = document?.getElementById(m?.t);
            const rootContainer = rawRootContainer instanceof HTMLDivElement ? rawRootContainer : undefined;

            if (!rootContainer) {
                continue;
            }
            if (rootContainer?.hasChildNodes() === true) {
                rootContainer.innerHTML = null;
            }

            reactContent[m?.i] = createRoot(rootContainer);

            const Skeleton = <m.Skeleton rootContainer={rootContainer} settings={settings} />;
            const SkeletonDetails = !isUndefinedOrNull(m.SkeletonDetails) ? <m.SkeletonDetails rootContainer={rootContainer} settings={settings} /> : '';
            reactContent?.[m?.i]?.render(<ThemeProvider theme={theme}>{isDetail ? SkeletonDetails : Skeleton}</ThemeProvider>);

            containers.push({
                allFacets: undefined,
                Component: m?.Component,
                id: m?.t,
                matomo: undefined,
                module: m?.i,
                rootContainer,
                settings,
                Skeleton,
                SkeletonDetails,
            });
        }

        if (isUndefinedNullOrEmpty(settings?.general?.idGroup)) {
            throw new ConfigError(ConfigErrorCauses.MISSING_IDGROUP);
        }

        let allFacets: Facets;
        try {
            abortControllerFacets?.abort();
            abortControllerFacets = new AbortController();
            const getAllFacets = (await import('./_tools/axios/cars/getAllFacets' /* webpackChunkName: "getAllFacets" */))?.default;
            allFacets = (await getAllFacets(settings?.general?.idGroup, abortControllerFacets?.signal, settings?.general?.debug))?.data?.facets;
            abortControllerFacets = null;
        } catch (err) {
            outputConsole('error', 'index', 'Failed to load all facets', err);
        }

        let matomo: MatomoSettings;
        try {
            matomo = (await getMatomoSettings(settings?.general?.idGroup, location?.hostname, settings?.general?.debug))?.data;
        } catch (err) {
            outputConsole('error', 'index', 'Failed to load matomo settings', err);
        }

        for (let i = 0; i < containers?.length; i++) {
            const container = containers?.[i];

            const content = (
                <ErrorHandler module={container?.module} settings={container?.settings}>
                    <Entry
                        allFacets={allFacets}
                        Component={container?.Component}
                        id={container?.id}
                        matomo={matomo}
                        module={container?.module}
                        rootContainer={container?.rootContainer}
                        settings={container?.settings}
                        Skeleton={container?.Skeleton}
                        SkeletonDetails={container?.SkeletonDetails}
                    />
                </ErrorHandler>
            );

            reactContent?.[container?.module]?.render(
                <ThemeProvider theme={theme}>
                    {/* ENVIRONMENT_FRONTEND === FrontendEnvironment.LIVE ? (
                        <PostHogProvider
                            apiKey={process.env.REACT_APP_POSTHOG_KEY}
                            options={{
                                api_host: POSTHOG_API_URL,
                                before_send: (event) => {
                                    event.properties['service_name'] = APPLICATION_NAME;
                                    return event;
                                },
                                cookieless_mode: 'on_reject',
                                defaults: '2025-05-24',
                                ui_host: POSTHOG_UI_URL,
                            }}
                        >
                            {content}
                        </PostHogProvider>
                    ) :  */(
                        content
                    )}
                </ThemeProvider>
            );
        }
    } catch (error) {
        try {
            const returnValue = await errorHandler(
                undefined,
                settings?.general?.language,
                'initReact',
                error,
                settings?.general?.idGroup,
                DBLogSource.Fleet,
                settings?.general?.debug,
                ENVIRONMENT_FRONTEND === FrontendEnvironment.LOCALSERVER
            );

            for (let i = 0; i < containers?.length; i++) {
                const container = containers?.[i];

                reactContent?.[container?.module]?.render(
                    <ThemeProvider theme={theme}>
                        <Suspense fallback={<ErrorCardSkeleton />}>
                            <ErrorCard
                                action={ErrorAction.RELOAD}
                                collapse={{
                                    code: returnValue?.output?.name,
                                    message: returnValue?.output?.message,
                                    stack: ENVIRONMENT_FRONTEND === FrontendEnvironment.LOCALSERVER || settings?.general?.debug ? returnValue?.output?.stack : undefined,
                                }}
                                description={returnValue?.output?.description}
                                heading={returnValue?.output?.heading}
                                level="error"
                                module={container?.module}
                                settings={settings}
                                translate={undefined}
                            />
                        </Suspense>
                    </ThemeProvider>
                );
            }
        } catch (err) {
            outputConsole('error', 'index', 'Failed to output error', err);
        }
    }
}

init();
